import { createSelector } from '@reduxjs/toolkit';
import { situationsAdapterSelectors } from '../redux/slices/situations';
import { usersAdapterSelectors } from 'src/redux/slices/users';

export const selectSituations = createSelector(
  [
    situationsAdapterSelectors.selectAll,
    (state) => state.monitoring.currentSituations,
  ],
  (situations, currentSituations) => {
    // Set lookup: with a large backlog this runs per dispatch, and
    // Array.includes made it O(n²) — enough to freeze the tab during bursts.
    const currentIds = new Set(currentSituations);
    return situations.filter((situation) => currentIds.has(situation.id));
  }
);

export const selectSituationLogs = (situationId) =>
  createSelector(
    (state) => situationsAdapterSelectors.selectById(state, situationId),
    (state) => usersAdapterSelectors.selectEntities(state),
    (situation, users) => {
      if (!situation?.log) {
        return [];
      }

      return situation.log
        .filter(({ type }) => type !== 'ASSIGNED_DEVICE_HISTORY')
        .map(({ uuid, type, createAt, ...log }) => ({
          ...log,
          id: uuid,
          action: type,
          date: createAt,
          user: log.userId ? (users[log.userId] ?? null) : null,
          eventCode: log.eventCode ?? null,
        }))
        .sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
    }
  );

export const selectSituationLogsDetails = (situationLog) =>
  createSelector(
    (state) => usersAdapterSelectors.selectEntities(state),
    (users) =>
      situationLog
        .map(({ uuid, type, createAt, ...log }) => ({
          ...log,
          id: uuid,
          action: type,
          date: createAt,
          user: log.userId ? (users[log.userId] ?? null) : null,
          deviceHistoryId: log.deviceHistoryId ?? null,
        }))
        .sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
  );

export const getSituationToProcess = (state) =>
  state.monitoring.situationToProcess;

export const getSituationProcessingStatus = (state) =>
  state.monitoring.isProcessing;

const getMonitoringMapStatusFromStore = (state) =>
  state.ui.monitoring.isMapOpen;

export const isMonitoringMapOpen = () =>
  createSelector(getMonitoringMapStatusFromStore, (mapStatus) => mapStatus);
